import { tableCoverageGroup } from '../utils/tableFieldGroups';

/** System columns available for grouping (non form-field). */
const SYSTEM_GROUP_IDS = Object.freeze([
  'machadalo_client_status',
  'assigned_to',
  'city',
]);

/** Hidden when exactly one table is already selected. */
const HIDE_WHEN_ONE_FORM = Object.freeze(['leads_form_id']);

const BUSINESS_SERVICE_DIMENSION = Object.freeze({
  id: 'campaign_id',
  label: 'Business service',
});

const DATE_FIELD_HINTS = Object.freeze(['date', 'time', 'created', 'updated']);

function isFormFieldDimension(dimension) {
  return String(dimension?.id || '').startsWith('field.');
}

function isDateLikeField(dimension) {
  const id = String(dimension?.id || '').toLowerCase();
  const label = String(dimension?.label || '').toLowerCase();
  if (id === 'created_at' || id.includes('created')) return true;
  return DATE_FIELD_HINTS.some((hint) => label.includes(hint) || id.includes(hint));
}

function sortGroupDimensions(dimensions) {
  const formColumns = [];
  const systemColumns = [];
  const otherColumns = [];

  dimensions.forEach((item) => {
    if (isFormFieldDimension(item)) {
      formColumns.push(item);
      return;
    }
    if (SYSTEM_GROUP_IDS.includes(item.id)) {
      systemColumns.push(item);
      return;
    }
    otherColumns.push(item);
  });

  return [...formColumns, ...systemColumns, ...otherColumns];
}

function formLabels(item) {
  return (item?.forms || []).map((form) => form.label).filter(Boolean);
}

function annotateTableCoverage(options) {
  const union = new Set();
  options.forEach((item) => {
    (item.form_ids || []).forEach((id) => union.add(String(id)));
  });

  if (union.size < 2) {
    return options.map((item) => ({ ...item, group: '', partial: false }));
  }

  return options
    .map((item) => {
      const formIds = (item.form_ids || []).map(String);
      const partial = formIds.length > 0 && formIds.length < union.size;
      const names = formLabels(item);
      return {
        ...item,
        group: tableCoverageGroup(partial, names),
        partial,
      };
    })
    .sort((left, right) => {
      if (left.group === right.group) return 0;
      if (!left.partial) return -1;
      if (!right.partial) return 1;
      return left.group.localeCompare(right.group);
    });
}

/**
 * Prepare dropdown options for the active chart type from merged catalog + table columns.
 */
export function prepareChartDimensions({
  dimensions = [],
  chartType,
  leadsFormIds = [],
  campaignIds = [],
  dataset = '',
}) {
  const selectedFormCount = leadsFormIds.filter(Boolean).length;
  const selectedServiceCount = campaignIds.filter(Boolean).length;
  const hasSelectedForm = selectedFormCount > 0;
  const source = [...dimensions];
  if (
    dataset === 'records'
    && selectedServiceCount !== 1
    && !source.some((item) => item.id === 'campaign_id')
  ) {
    source.unshift({ ...BUSINESS_SERVICE_DIMENSION });
  }

  let filtered = source.filter((item) => {
    if (selectedFormCount === 1 && HIDE_WHEN_ONE_FORM.includes(item.id)) {
      return false;
    }
    if (selectedServiceCount === 1 && item.id === 'campaign_id') {
      return false;
    }
    if (hasSelectedForm && SYSTEM_GROUP_IDS.includes(item.id)) {
      return false;
    }
    return Boolean(item?.id);
  });

  if (chartType === 'line') {
    const dateFormFields = filtered.filter(isDateLikeField);
    const timeFieldOptions = dateFormFields.length
      ? dateFormFields
      : [{
        id: 'created_at',
        label: dataset === 'whatsapp_templates' || dataset === 'intervene' ? 'Date' : 'Created date',
      }];
    return {
      groupByOptions: [],
      timeFieldOptions,
      needsTableColumns: dataset === 'records' && !hasSelectedForm,
    };
  }

  if (chartType === 'number') {
    return {
      groupByOptions: [],
      timeFieldOptions: [],
      needsTableColumns: false,
    };
  }

  const groupByOptions = annotateTableCoverage(
    sortGroupDimensions(filtered.filter((item) => !isDateLikeField(item) || isFormFieldDimension(item)))
  );

  return {
    groupByOptions,
    timeFieldOptions: [],
    needsTableColumns: dataset === 'records' && selectedServiceCount === 0 && !hasSelectedForm && chartType !== 'number',
  };
}

export function resolveScopeFormIds({
  dimension,
  groupByOptions = [],
  aggregation = 'count',
  measureField,
  measureFieldOptions = [],
}) {
  const picks = [];
  const dimensionOption = groupByOptions.find((item) => item.id === dimension);
  if (dimensionOption?.partial) picks.push(dimensionOption.form_ids || []);
  if (aggregation !== 'count' && measureField) {
    const measureOption = measureFieldOptions.find((item) => item.id === measureField);
    if (measureOption?.partial) picks.push(measureOption.form_ids || []);
  }
  if (!picks.length) return [];
  return picks
    .map((ids) => ids.map(String))
    .reduce((current, ids) => current.filter((id) => ids.includes(id)));
}

export { isFormFieldDimension, isDateLikeField };

export default prepareChartDimensions;
